import {Pipe, PipeTransform} from '@angular/core';
import {candidateStatusForId} from '../../utils';
import {CandidateStatus} from '../../model';
import * as compareAsc from 'date-fns/compare_asc';
import * as compareDesc from 'date-fns/compare_desc';
import * as subDays from 'date-fns/sub_days';

export interface TalentCardSearchOptions {
  hiddenIds?: string[];
  rateRange?: number;
  sort?: string;
  favorites?: string[];
  query?: string;
  filter?: string;
  limitTo?: number;
}

const RECENTLY_ADDED_DAYS = 14;

@Pipe({name: 'talentCard'})
export class TalentCardPipe implements PipeTransform {

  transform(submittals: Submittal[], options?: TalentCardSearchOptions, positionId?: string): Submittal[] {
    const submittalMap = new Map<string, Submittal>();

    // Deduplicate by person ID; keeping only the latest
    submittals.forEach(submittal => {
      if (!this.predicate(submittal, options, positionId)) {
        return;
      }
      if (submittalMap.has(submittal.personId)) {
        const prevSubmittal = submittalMap.get(submittal.personId);
        if (submittal.date <= prevSubmittal.date) return;
      }
      submittalMap.set(submittal.personId, submittal);
    });

    return submittals ? Array.from(submittalMap.values())
      .sort((a, b) => TalentCardPipe.comparator(a, b, options))
      .slice(0, (options && options.limitTo) || undefined) : [];
  }

  private predicate(submittal: Submittal, options?: TalentCardSearchOptions, positionId?: string): boolean {
    if (!submittal.profile || !submittal.profile.profileName) {
      return false;
    }
    if (positionId && submittal.orderId !== positionId) return false;
    const status = candidateStatusForId(submittal.statusId);
    if (status === CandidateStatus.rejected || status === CandidateStatus.withdrawn) {
      return false;
    }
    if (options) {
      let rateRange = Number(options.rateRange);
      if (rateRange && (submittal.rate < rateRange * 10 || submittal.rate >= (rateRange + 1) * 10)) {
        return false;
      }
      if (options.filter) {
        switch (options.filter) {
          case 'previously-hired':
            if (!submittal.workedWithMe || submittal.workingWithMe) return false;
            break;
          case 'currently-working':
            if (!submittal.workingWithMe) return false;
            break;
          case 'favorited':
            if (!options.favorites || !options.favorites.includes(submittal.personId)) return false;
            break;
          case 'recently-added':
            if (submittal.date < subDays(new Date(), RECENTLY_ADDED_DAYS)) return false;
            break;
        }
      }
      if (options.query && !this.searchProfile(submittal, options.query)) {
        return false;
      }
    }
    return true;
  }

  private searchProfile(submittal: Submittal, query: string): boolean {
    let fields = [
      submittal.profile.profileName,
      submittal.profile.title,
      submittal.profile.summary
    ], terms = query ? query.split(' ') : [];
    if (submittal.independentAssessment) {
      fields.push(submittal.independentAssessment.summary);
    }
    for (let i in fields) {
      let field = (fields[i] || '').toLowerCase();
      if (terms.every((term) => field.indexOf(term.toLowerCase()) !== -1)) {
        return true;
      }
    }
    return false;
  }

  private static comparator(a: Submittal, b: Submittal, options?: TalentCardSearchOptions): number {
    let sort = 0;
    if (options) {
      if (options.hiddenIds)
        if (options.hiddenIds.includes(a.personId)) {
          if (!options.hiddenIds.includes(b.personId))
            return 1;
        } else if (options.hiddenIds.includes(b.personId))
          return -1;
      switch (options.sort) {
        case 'date-desc':
          sort = compareDesc(a.date, b.date);
          break;
        case 'date-asc':
          sort = compareAsc(a.date, b.date);
          break;
        case 'rate-asc':
          if (a.rate) {
            if (!b.rate) return -1;
          } else if (b.rate) return 1; else break;
          sort = a.rate - b.rate;
          break;
        case 'rate-desc':
          if (a.rate) {
            if (!b.rate) return -1;
          } else if (b.rate) return 1; else break;
          sort = b.rate - a.rate;
          break;
        case 'rating':
          if (a.rating) {
            if (!b.rating) return -1;
          } else if (b.rating) return 1; else break;
          sort = b.rating - a.rating;
          break;
        case 'availability':
          sort = compareDesc(a.availabilityUpdated, b.availabilityUpdated);
          break;
      }
    }
    if (!sort) return a.profile.profileName.localeCompare(b.profile.profileName);
    return sort;
  }
}
